import { useState } from "react";
import { useParams } from "react-router";
import { useOutletContext, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  IconButton,
  Stack,
  Avatar as MUIAvatar,
  Tooltip,
  Paper,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

import Avatar from "../../assets/img/avatar.png";
import PhotoPopUpMessage from "../../components/PopUp/PhotoPopUpMessage";
import Loader from "../../components/Loader";
import CompareDialog from "../../components/images/CompareDialog";
import UploadFilesModal from "../../components/Modals/FileUploadModal";
import EcrfBlocks from "../../components/Trials/EcrfBlocks";
import useGetExternalMediaCase from "../../hooks/useGetExternalMediaCase";
import useDeleteExternalMediaMutation from "../../hooks/useDeleteExternalMediaMutation";
import useGetExternalMediaTypes from "../../hooks/useGetExternalMediaTypes";
import useGetVisitExternalPhotoTypes from "../../hooks/useGetVisitExternalPhotoTypes";
import useGetVisitExternalVideoTypes from "../../hooks/useGetVisitExternalVideoTypes";
import { INV_RELATED_ROLES, UPLOAD_TYPES, USER_ROLES } from "../../utils/constants";

dayjs.extend(utc);

const ExternalMediaPatientCase = ({ mediaType = "photo" }) => {
  const { _id_, caseId } = useParams();
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const isPhoto = mediaType === "photo";
  const activeKey = isPhoto ? "externalPhotos" : "externalVideos";
  const label = isPhoto ? "Photos" : "Videos";

  const location = useLocation();
  const visitName =
    location.state?.visitName || new URLSearchParams(location.search).get("visitName");

  const { setActive, setBreadcrumbTitle, isTrialLocked, trialFilter } = useOutletContext();
  setActive(activeKey);
  setBreadcrumbTitle(`External ${label}`);

  const [popMessage, setPopMessage] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [selectedCompareImage, setSelectedCompareImage] = useState(null);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);

  const {
    data: externalMedia,
    isLoading: loading,
    refetch: refetchExternalMedia,
  } = useGetExternalMediaCase({ caseId, mediaType });
  const deleteExternalMediaMutation = useDeleteExternalMediaMutation();

  const visitId = externalMedia?.visit_id;

  const [files, setFiles] = useState([]);
  const [captureDateTime, setCaptureDateTime] = useState(dayjs().utc());
  const [modal, setModal] = useState({ open: false, lockedTypeId: null });

  const { data: configuredPhotoTypes = [] } = useGetVisitExternalPhotoTypes({
    visitId,
    enabled: !!visitId && isPhoto,
  });
  const { data: configuredVideoTypes = [] } = useGetVisitExternalVideoTypes({
    visitId,
    enabled: !!visitId && !isPhoto,
  });
  const { data: fullCatalog = [] } = useGetExternalMediaTypes({
    type: isPhoto ? "photos" : "videos",
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const cardTypes = isPhoto ? configuredPhotoTypes : configuredVideoTypes;

  const openModal = (lockedTypeId = null) => {
    setFiles([]);
    setModal({ open: true, lockedTypeId });
  };

  const handleOnImageClick = (img) => {
    setShowCompare(true);
    setSelectedCompareImage(img);
  };

  const deleteExternalMedia = (id) => {
    setDeleteItemId(id);
    setPopMessage(true);
  };

  const confirmDelete = (id) => {
    if (id) {
      deleteExternalMediaMutation.mutate(
        { id, caseId },
        {
          onSuccess: () => {
            toast.success("Media Deleted Successfully");
          },
          onError: () => {
            toast.error("Something Went Wrong");
          },
          onSettled: () => {
            setPopMessage(false);
          },
        }
      );
    }
  };

  const downloadFile = (url, filename) => {
    axios(url, {
      responseType: "blob",
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "image/jpg" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    });
  };

  const generateFileName = (data) => {
    const {
      capture_datetime,
      trial_name,
      visit_number,
      photo_type_name,
      video_type_name,
      participant_id,
      site_id,
      name_initials,
    } = data;
    const typeName = isPhoto ? photo_type_name : video_type_name;
    return `${capture_datetime}__Appiell__${trial_name}__${site_id}__${participant_id}(${name_initials})__${visit_number}__${typeName}`;
  };

  const isUploaderAllowed =
    !isTrialLocked &&
    role !== "CRO Admin" &&
    role !== "Cro_coordinator" &&
    role !== "Data Manager" &&
    role !== "Reviewer";

  const showScoreButton = isPhoto && Boolean(externalMedia?.external_score_ecrf_name);
  const canScore =
    !isTrialLocked && (INV_RELATED_ROLES.includes(role) || role === USER_ROLES.REVIEWER);

  const mediaRolesAllowed = [
    "Investigator",
    "Sub_investigator",
    "Inv_coordinator",
    "CRO Admin",
    "Cro_coordinator",
    "Super Admin",
    "Data Manager",
    "Reviewer",
  ];

  const collectItems = () => {
    if (!externalMedia?.data) return [];
    return externalMedia.data.flatMap(
      (session) =>
        session?.[isPhoto ? "external_session_media_photo" : "external_session_media_video"] || []
    );
  };

  const items = collectItems();

  const renderDate = (dt) => {
    if (!dt) return "-";
    return dayjs.utc(dt).local().format("YYYY-MM-DD HH:mm");
  };

  const uploadedTypeIds = new Set(
    items
      .map((item) => (isPhoto ? item.photo_type_id : item.video_type_id))
      .filter((id) => id != null)
  );
  const emptyCardTypes = cardTypes.filter((type) => !uploadedTypeIds.has(type.id));

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <Loader />
          </Box>
        ) : (
          <>
            <Typography variant="h6" mb={3}>
              {visitName}
            </Typography>

            {mediaRolesAllowed.includes(role) && (
              <Box sx={{ mt: 2 }}>
                <Stack spacing={1} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {label} ({items.length})
                  </Typography>

                  {items.length === 0 && emptyCardTypes.length === 0 ? (
                    <Typography color="text.secondary">
                      No external {label.toLowerCase()}
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {isUploaderAllowed &&
                        emptyCardTypes.map((type) => (
                          <Grid item xs={12} sm={6} md={3} key={`empty-${type.id}`}>
                            <Card
                              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                              variant="outlined"
                            >
                              <CardActionArea onClick={() => openModal(type.id)}>
                                <Box
                                  sx={{
                                    height: 180,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    bgcolor: "grey.100",
                                  }}
                                >
                                  {isPhoto ? (
                                    <ImageIcon color="disabled" sx={{ fontSize: 48 }} />
                                  ) : (
                                    <VideocamIcon color="disabled" sx={{ fontSize: 48 }} />
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                  >
                                    Upload
                                  </Button>
                                </Box>
                              </CardActionArea>
                              <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {type.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Not uploaded yet
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}

                      {items.map((data) => (
                        <Grid item xs={12} sm={6} md={3} key={data.id}>
                          <Card
                            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                            variant="outlined"
                          >
                            {isPhoto ? (
                              data.image ? (
                                <CardMedia
                                  component="img"
                                  height="180"
                                  image={data.image}
                                  alt={data.photo_type_name || "photo"}
                                  sx={{ cursor: "pointer", objectFit: "cover" }}
                                  onClick={() => handleOnImageClick(data.image)}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 180,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <MUIAvatar
                                    alt="avatar"
                                    src={Avatar}
                                    sx={{ width: 80, height: 80 }}
                                  />
                                </Box>
                              )
                            ) : data.video ? (
                              <Box
                                sx={{
                                  height: 180,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "black",
                                }}
                              >
                                <video
                                  src={data.video}
                                  playsInline
                                  controls
                                  style={{
                                    maxHeight: "100%",
                                    maxWidth: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  height: 180,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>No video</Typography>
                              </Box>
                            )}

                            <CardContent sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {(isPhoto ? data?.photo_type_name : data?.video_type_name) || "-"}
                              </Typography>

                              <Stack>
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Visit Name:</strong> {data?.visit_number || "-"}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                  <strong>Captured At:</strong> {renderDate(data?.capture_datetime)}
                                </Typography>
                              </Stack>
                            </CardContent>

                            {showScoreButton && (
                              <Box sx={{ px: 1, pb: 1 }}>
                                <Button
                                  fullWidth
                                  data-testid="external-score-ecrf-btn"
                                  variant="contained"
                                  startIcon={<AssignmentTurnedInOutlinedIcon />}
                                  disabled={!canScore}
                                  onClick={() => setScoreDialogOpen(true)}
                                >
                                  Score in eCRF
                                </Button>
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1,
                              }}
                            >
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    downloadFile(
                                      isPhoto ? data.image : data.video,
                                      `${generateFileName(data)}.${isPhoto ? "jpg" : "mp4"}`
                                    );
                                  }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>

                              {isUploaderAllowed && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => deleteExternalMedia(data?.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {isUploaderAllowed && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => openModal(null)}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Add Extra {isPhoto ? "Photo" : "Video"}
                    </Button>
                  )}
                </Stack>

                {isUploaderAllowed && (
                  <UploadFilesModal
                    uploadType={
                      isPhoto ? UPLOAD_TYPES.EXTERNAL_PHOTOS : UPLOAD_TYPES.EXTERNAL_VIDEOS
                    }
                    showModal={modal.open}
                    setShowModal={(open) => setModal((prev) => ({ ...prev, open }))}
                    files={files}
                    setFiles={setFiles}
                    captureDateTime={captureDateTime}
                    setCaptureDateTime={setCaptureDateTime}
                    acceptedMimeTypes={
                      isPhoto
                        ? ["image/gif", "image/jpeg", "image/png", "image/svg+xml"]
                        : ["video/mp4", "video/mpeg", "video/webm", "video/quicktime"]
                    }
                    typeList={fullCatalog}
                    caseId={caseId}
                    getExternalMedia={refetchExternalMedia}
                    trialId={trialFilter}
                    lockedTypeId={modal.lockedTypeId}
                  />
                )}

                {isPhoto && (
                  <CompareDialog
                    open={showCompare}
                    images={items}
                    onClose={() => setShowCompare(false)}
                    selectedImage={selectedCompareImage}
                  />
                )}

                {scoreDialogOpen && (
                  <EcrfBlocks
                    visit_number={externalMedia?.ecrf_visit_number}
                    visitName={externalMedia?.visit_name || visitName}
                    trialId={externalMedia?.trial_id}
                    trialName={externalMedia?.trial_name}
                    selectedBlock={externalMedia?.external_score_ecrf_name}
                    fromQueryTable={true}
                    useCompactModal={true}
                    showEcrfBlock={scoreDialogOpen}
                    setShowEcrfBlock={setScoreDialogOpen}
                    patientId={_id_}
                    isTrialLocked={isTrialLocked}
                    selectedSessionId={caseId}
                  />
                )}

                {popMessage && (
                  <PhotoPopUpMessage
                    setPopMessage={setPopMessage}
                    confirmDelete={confirmDelete}
                    id={deleteItemId}
                    type={mediaType}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ExternalMediaPatientCase;
